import { HierarcicalObjectReference, ObjectData, ObjectDB, ObjectId, SearchOptions } from "@novorender/data-js-api";
import { isResponseError } from "@procore/core-http";

import { hasSceneMetadataStream, SceneMetadataStreamer } from "apis/procore/bimSceneMetadata";
// Deep import rather than the `dataAccess` barrel, which exports `ObjectDbDataAccess` —
// built on this class, so the barrel would pull it back into every consumer here.
import { ObjectMetadataCache } from "dataAccess/objectMetadataCache";
import { DeepMutable } from "types/misc";
import { SelectionSet } from "utils/selectionSet";

interface SearchOptionsExt extends SearchOptions {
    skipPermissionCheck?: boolean;
}

/** Optional ID mapping added by Procore (or future legacy) ObjectDB implementations. */
type ObjectDBWithResolve = ObjectDB & {
    resolveObjectIdsToInstanceIds?(objectIds: ObjectId[], signal: AbortSignal | undefined): Promise<(string | null)[]>;
    resolveInstanceIdsToObjectIds?(
        instanceIds: string[],
        signal: AbortSignal | undefined,
    ): Promise<(ObjectId | null)[]>;
};

/**
 * Some backends (e.g. `ProcoreObjectDB`) accept a `fastPathOnly` flag on
 * `descendants` to opt out of the unbounded subtree-search fallback. Backends
 * without it simply ignore the extra argument.
 */
type ObjectDBWithFastPathDescendants = ObjectDB & {
    descendants(
        object: HierarcicalObjectReference,
        signal: AbortSignal | undefined,
        fastPathOnly?: boolean,
        skipFastPath?: boolean,
    ): Promise<ObjectId[]>;
};

/**
 * Wrapper around ObjectDB with some additional features
 * - Caches metadata for requests by object ID
 * - Filters out objects that the user don't have permissions to by default
 *
 * Also it patches `loadCompleteMetadata` with caching
 *
 * This is the object DB's own surface, not an app-wide one: `search` takes a query
 * language only this backend speaks, so a caller here is a caller that needs the
 * network. It stays as what `ObjectDbDataAccess` (`src/dataAccess`) is built on,
 * while feature code moves to `ObjectDataAccess` — see the note on the deprecated
 * `db` in `explorerGlobals`.
 */
export class AppObjectDB implements ObjectDB {
    private readonly metadataCache = new ObjectMetadataCache();
    /**
     * Whether the /resolve endpoint is available for this session.
     * undefined - unknown, true - available, false - unavailable
     */
    private _resolveEndpointAvailable: boolean | undefined = undefined;

    get isResolveEndpointAvailable() {
        return this._resolveEndpointAvailable;
    }

    get metadataCacheStats() {
        return this.metadataCache.stats;
    }

    constructor(
        private readonly db: ObjectDB,
        private readonly availableIds: SelectionSet | undefined,
    ) {
        const patchedDb = db as ObjectDB & { loadCompleteMetadata: (id: number) => Promise<ObjectData> };
        const originalLoadCompleteMetadata = patchedDb.loadCompleteMetadata;
        patchedDb.loadCompleteMetadata = (id: number) => {
            return this.metadataCache.getOrLoadMetadata(id, () => originalLoadCompleteMetadata(id));
        };
    }

    async getObjectMetdata(id: number, skipPermissionCheck = false): Promise<ObjectData> {
        if (skipPermissionCheck || !this.availableIds || this.availableIds.has(id)) {
            return this.metadataCache.getOrLoadMetadata(id, () => this.db.getObjectMetdata(id));
        }

        throw new ObjectAccessForbiddenError(id);
    }

    async *search(
        { skipPermissionCheck, ...filter }: SearchOptionsExt,
        signal: AbortSignal | undefined,
    ): AsyncIterableIterator<HierarcicalObjectReference> {
        for await (const obj of this.db.search(filter, signal)) {
            if (skipPermissionCheck || !this.availableIds || this.availableIds.has(obj.id)) {
                yield obj;
            }
        }
    }

    /**
     * Expose the wrapped db's bulk scene-metadata stream, if it supports one
     * (e.g. `ProcoreObjectDB`). Returns `undefined` for backends without a
     * metadata dump so callers can fall back to `search`.
     */
    getSceneMetadataStreamer(): SceneMetadataStreamer | undefined {
        return hasSceneMetadataStream(this.db) ? this.db : undefined;
    }

    async descendants(
        object: HierarcicalObjectReference,
        signal: AbortSignal | undefined,
        skipPermissionCheck = false,
        fastPathOnly = false,
        skipFastPath = false,
    ): Promise<ObjectId[]> {
        if (skipPermissionCheck || !this.availableIds || this.availableIds.has(object.id)) {
            const ids = await (this.db as ObjectDBWithFastPathDescendants).descendants(
                object,
                signal,
                fastPathOnly,
                skipFastPath,
            );

            // Bug in descendants caches [] when aborted
            if (signal?.aborted) {
                (object as DeepMutable<HierarcicalObjectReference>).descendants = undefined;
                signal.throwIfAborted();
            }

            return ids;
        }

        throw new ObjectAccessForbiddenError(object.id);
    }

    /**
     * Batch-resolve object ids → Procore/InstanceId strings. Returns `undefined` if the endpoint
     * resolution index is not available for the scene; callers should fall back to search.
     */
    async resolveObjectIdsToInstanceIds(
        objectIds: ObjectId[],
        signal: AbortSignal | undefined,
    ): Promise<(string | null)[] | undefined> {
        if (this._resolveEndpointAvailable === false) {
            return undefined;
        }
        const inner = this.db as ObjectDBWithResolve;
        if (!inner.resolveObjectIdsToInstanceIds) {
            this._resolveEndpointAvailable = false;
            return undefined;
        }
        try {
            const data = await inner.resolveObjectIdsToInstanceIds(objectIds, signal);
            this._resolveEndpointAvailable = true;
            return data.map((value, i) => {
                const id = objectIds[i]!;
                if (this.availableIds && !this.availableIds.has(id)) {
                    return null;
                }
                return value;
            });
        } catch (e) {
            if (isResponseError(e) && e.status === 404) {
                this._resolveEndpointAvailable = false;
                return undefined;
            }
            throw e;
        }
    }

    /**
     * Batch-resolve Procore/InstanceId strings → object ids. See {@link resolveObjectIdsToInstanceIds}.
     */
    async resolveInstanceIdsToObjectIds(
        instanceIds: string[],
        signal: AbortSignal | undefined,
    ): Promise<(ObjectId | null)[] | undefined> {
        if (this._resolveEndpointAvailable === false) {
            return undefined;
        }
        const inner = this.db as ObjectDBWithResolve;
        if (!inner.resolveInstanceIdsToObjectIds) {
            this._resolveEndpointAvailable = false;
            return undefined;
        }
        try {
            const data = await inner.resolveInstanceIdsToObjectIds(instanceIds, signal);
            this._resolveEndpointAvailable = true;
            return data.map((value) => {
                if (value === null) {
                    return null;
                }
                if (this.availableIds && !this.availableIds.has(value)) {
                    return null;
                }
                return value;
            });
        } catch (e) {
            if (isResponseError(e) && e.status === 404) {
                this._resolveEndpointAvailable = false;
                return undefined;
            }
            throw e;
        }
    }
}

export class ObjectAccessForbiddenError extends Error {
    constructor(public readonly objectId: ObjectId) {
        super(`Object ${objectId} is not available`);
    }
}
